import { Controller, FormProvider, useForm } from "react-hook-form";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { ReplyIcon } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Field, FieldError } from "@/components/ui/field";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

import { CreateReplySchema, createReplySchema } from "@/features/tickets/schemas";
import { createTicketCommentReply } from "@/features/tickets/services/ticket";
import Error from "@/shared/components/Error";
import { PermissionTooltip } from "@/shared/components/PermissionTooltip";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { getMyDetails } from "@/shared/lib/services/user";
import { AppError } from "@/shared/lib/utils/errors";

interface TicketCommentReplyComposerProps {
  projectId: string;
  ticketId: string;
  /** The top-level comment being replied to (API rejects replies to replies). */
  commentId: string;
  /** Whether the caller has PROJECT_TICKET_COMMENTS_CREATE for this project. */
  canCreateComments: boolean;
  /** Called when the composer should close (after a successful reply or on cancel). */
  onClose: () => void;
}

export function TicketCommentReplyComposer({ projectId, ticketId, commentId, canCreateComments, onClose }: TicketCommentReplyComposerProps) {
  const queryClient = useQueryClient();

  const { data: user, isPending: isUserPending } = useQuery({
    queryKey: [QUERY_KEYS.GET_MY_DETAILS],
    queryFn: getMyDetails,
  });

  const form = useForm<CreateReplySchema>({
    resolver: zodResolver(createReplySchema),
    defaultValues: {
      projectId,
      ticketId,
      commentId,
      body: "",
    },
  });

  const mutation = useMutation<string, AppError | Error, CreateReplySchema>({
    mutationFn: createTicketCommentReply,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TICKET_COMMENTS, projectId, ticketId], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TICKET_BY_ID, projectId, ticketId], exact: true });
      form.reset({ projectId, ticketId, commentId, body: "" });
      onClose();
    },
  });

  const initials = user ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase() : null;
  const disabled = mutation.isPending || !canCreateComments;

  function handleCancel() {
    form.reset({ projectId, ticketId, commentId, body: "" });
    onClose();
  }

  async function onSubmit(values: CreateReplySchema) {
    mutation.mutate(values);
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3 pt-2">
        {mutation.isError && <Error title="Unable to post reply" error={mutation.error} />}
        <div className="flex gap-3">
          {isUserPending ? (
            <Skeleton className="size-6 shrink-0 rounded-full" />
          ) : (
            <Avatar size="sm">
              <AvatarFallback className="text-[10px]">{initials ?? "-"}</AvatarFallback>
            </Avatar>
          )}
          <div className="flex flex-1 flex-col gap-2">
            <Controller
              name="body"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <PermissionTooltip allowed={canCreateComments} message="You don't have permission to comment on tickets in this project." className="w-full">
                    <Textarea {...field} placeholder="Write a reply…" disabled={disabled} aria-invalid={fieldState.invalid} className="min-h-16" />
                  </PermissionTooltip>
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" type="button" onClick={handleCancel} disabled={mutation.isPending}>
                Cancel
              </Button>
              <PermissionTooltip allowed={canCreateComments} message="You don't have permission to comment on tickets in this project.">
                <Button size="sm" type="submit" disabled={disabled || !form.watch("body").trim()}>
                  <ReplyIcon className="size-3.5" />
                  {mutation.isPending ? "Replying..." : "Reply"}
                </Button>
              </PermissionTooltip>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
